import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@/lib/cn";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const PAD = (n: number) => String(n + 1).padStart(2, "0");
const CYCLE_MS = 5000;
const RESUME_MS = 8000;

const TABS = [
  {
    title: "Identity & access",
    description:
      "Enterprise SSO with SAML and OIDC. Role-based access control across every agent. Session management, MFA enforcement, and audit-logged authentication events. Your identity provider stays the source of truth.",
    terminal: `$ cosx iam status
┌─────────────────────────────────────┐
│  SSO Provider    Azure AD   ✓ live  │
│  MFA Enforced    All users  ✓       │
│  Active Sessions 142                │
│  RBAC Policies   12 active          │
└─────────────────────────────────────┘
Last auth event: 2m ago
Session token rotation: 15 min
✓ All identity checks passing`,
  },
  {
    title: "Billing & metering",
    description:
      "Usage-based billing with per-agent metering. Real-time cost dashboards, configurable alerts, and exportable invoices. Every API call, document processed, and compute minute is tracked and attributable to a team or project.",
    terminal: `$ cosx billing summary --period=mar-2026
┌──────────────────────────────────────┐
│  Agent        Calls     Cost         │
│  ─────        ─────     ────         │
│  XENI         12,847    £2,140.20    │
│  Robolab X     8,203      £684.10    │
│  Metaroom      3,419      £512.85    │
├──────────────────────────────────────┤
│  Total                  £3,337.15    │
└──────────────────────────────────────┘
✓ Budget alert: 68% of monthly limit`,
  },
  {
    title: "Data residency",
    description:
      "Choose where your data lives. Region-locked storage with configurable replication policies. Data never leaves your selected jurisdiction unless you explicitly allow it. Encryption at rest and in transit with customer-managed keys.",
    terminal: `$ cosx data residency --show
┌─────────────────────────────────────┐
│  Primary Region    eu-west-2 (UK)   │
│  Replication       eu-west-1 (IR)   │
│  Encryption        AES-256-GCM      │
│  Key Management    Customer-managed  │
│  Data Transfer     Restricted        │
└─────────────────────────────────────┘
✓ No cross-border transfers detected
✓ Encryption keys rotated: 6 days ago
✓ Compliance: UK GDPR aligned`,
  },
];

export function InfrastructureConsole() {
  const prefersReducedMotion = useReducedMotion();

  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [progressKey, setProgressKey] = useState(0);

  const playing = visible && !userPaused && !prefersReducedMotion;

  // Track visibility
  useEffect(() => {
    if (prefersReducedMotion) return;
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!!entry?.isIntersecting),
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  // Auto-cycle
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setActive((prev) => {
        const next = (prev + 1) % TABS.length;
        setProgressKey((k) => k + 1);
        return next;
      });
    }, CYCLE_MS);
    return () => clearInterval(id);
  }, [playing]);

  const handleTabClick = useCallback(
    (i: number) => {
      setActive(i);
      setProgressKey((k) => k + 1);
      setUserPaused(true);

      if (resumeTimer.current) clearTimeout(resumeTimer.current);
      if (!prefersReducedMotion) {
        resumeTimer.current = setTimeout(() => setUserPaused(false), RESUME_MS);
      }
    },
    [prefersReducedMotion],
  );

  const handleMouseEnter = useCallback(() => {
    if (prefersReducedMotion) return;
    setUserPaused(true);
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
  }, [prefersReducedMotion]);

  const handleMouseLeave = useCallback(() => {
    if (prefersReducedMotion) return;
    resumeTimer.current = setTimeout(() => setUserPaused(false), RESUME_MS);
  }, [prefersReducedMotion]);

  useEffect(() => {
    return () => {
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Mobile: horizontal tab bar with progress */}
      <div className="flex overflow-x-auto border-b border-[var(--color-border)] mb-6 md:hidden" role="tablist">
        {TABS.map((tab, i) => (
          <button
            key={tab.title}
            role="tab"
            aria-selected={active === i}
            onClick={() => handleTabClick(i)}
            className={cn(
              "relative flex-shrink-0 px-4 py-3 text-sm font-[family-name:var(--font-mono)] tracking-wider transition-colors cursor-pointer whitespace-nowrap",
              active === i
                ? "text-[var(--color-fg)]"
                : "text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg-secondary)]",
            )}
          >
            {PAD(i)} · {tab.title}
            {active === i && (
              <span
                key={progressKey}
                className="absolute bottom-0 left-0 h-0.5 bg-[var(--color-fg)]"
                style={
                  playing
                    ? {
                        width: "100%",
                        animation: `sidebar-progress-h ${CYCLE_MS}ms linear`,
                      }
                    : { width: "100%" }
                }
              />
            )}
          </button>
        ))}
      </div>

      {/* Desktop: 3-column layout */}
      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr] md:gap-8 gap-6">
        {/* Vertical nav rail (desktop only) */}
        <div
          className="hidden md:block relative"
          role="tablist"
          aria-orientation="vertical"
        >
          {/* Track line */}
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-[var(--color-border)]" />

          <div className="flex flex-col">
            {TABS.map((tab, i) => (
              <button
                key={tab.title}
                role="tab"
                aria-selected={active === i}
                onClick={() => handleTabClick(i)}
                className={cn(
                  "relative text-left pl-5 pr-3 py-4 transition-colors cursor-pointer",
                  active === i
                    ? "text-[var(--color-fg)]"
                    : "text-[var(--color-fg-tertiary)] hover:text-[var(--color-fg-secondary)]",
                )}
              >
                {/* Vertical progress indicator */}
                {active === i && (
                  <span
                    key={progressKey}
                    className="absolute left-0 top-0 w-0.5 bg-[var(--color-fg)]"
                    style={
                      playing
                        ? {
                            height: "100%",
                            animation: `sidebar-progress ${CYCLE_MS}ms linear`,
                          }
                        : { height: "100%" }
                    }
                  />
                )}
                <span className="block text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)] mb-1">
                  {PAD(i)}
                </span>
                <span className="block text-sm font-[family-name:var(--font-mono)] tracking-wider">
                  {tab.title}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Description panel */}
        <div className="relative min-h-[260px] flex flex-col justify-center">
          {TABS.map((tab, i) => (
            <div
              key={tab.title}
              className={cn(
                prefersReducedMotion ? "" : "transition-opacity duration-500 ease-out",
                i === 0 ? "relative" : "absolute inset-0 flex flex-col justify-center",
              )}
              style={{
                opacity: active === i ? 1 : 0,
                pointerEvents: active === i ? "auto" : "none",
              }}
              role="tabpanel"
              aria-hidden={active !== i}
            >
              <h3 className="text-xl font-medium mb-3 font-[family-name:var(--font-display)]">
                {tab.title}
              </h3>
              <p className="text-sm text-[var(--color-fg-secondary)] leading-relaxed max-w-md">
                {tab.description}
              </p>
            </div>
          ))}
        </div>

        {/* Terminal mockup */}
        <div className="relative">
          <div className="bg-[#0a0a0a] border border-[#222] rounded-lg overflow-hidden">
            {/* Title bar */}
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-[#222]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
            </div>
            {/* Terminal content */}
            <div className="relative min-h-[260px]">
              {TABS.map((tab, i) => (
                <pre
                  key={tab.title}
                  className={cn(
                    "p-4 text-xs font-[family-name:var(--font-mono)] leading-relaxed text-gray-300 whitespace-pre overflow-x-auto",
                    prefersReducedMotion ? "" : "transition-opacity duration-500 ease-out",
                    i === 0 ? "relative" : "absolute inset-0",
                  )}
                  style={{
                    opacity: active === i ? 1 : 0,
                    pointerEvents: active === i ? "auto" : "none",
                  }}
                >
                  {tab.terminal.split("\n").map((line, li) => (
                    <span key={li} className="block">
                      {line.includes("✓") ? (
                        <span>
                          {line.split("✓").map((part, pi, arr) =>
                            pi < arr.length - 1 ? (
                              <span key={pi}>
                                {part}
                                <span className="text-emerald-400">✓</span>
                              </span>
                            ) : (
                              <span key={pi}>{part}</span>
                            ),
                          )}
                        </span>
                      ) : line.startsWith("$") ? (
                        <span className="text-emerald-400">{line}</span>
                      ) : (
                        line
                      )}
                    </span>
                  ))}
                  <span className="inline-block w-1.5 h-3.5 bg-emerald-400 animate-[cursorBlink_1s_step-end_infinite] ml-0.5 align-middle" />
                </pre>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes sidebar-progress {
          from { height: 0%; }
          to { height: 100%; }
        }
        @keyframes sidebar-progress-h {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
